import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const AuthContext = createContext(null);

// Secure token storage — sessionStorage (cleared on tab close, not persistent like localStorage)
// Primary auth is via httpOnly cookies set by backend
const TOKEN_KEY = 'aira_session';
const getStoredToken = () => sessionStorage.getItem(TOKEN_KEY);
const setStoredToken = (token) => sessionStorage.setItem(TOKEN_KEY, token);
const clearStoredToken = () => { sessionStorage.removeItem(TOKEN_KEY); };

// Create axios instance with credentials (sends httpOnly cookies automatically)
const apiClient = axios.create({ baseURL: API, withCredentials: true });

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    const token = getStoredToken();
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const { data } = await apiClient.get('/auth/me', { headers });
      setUser({ ...data, token: token || 'cookie' });
    } catch (err) {
      const status = err?.response?.status;
      if (status !== 401) console.error('Auth check failed:', status || err.message);
      if (status === 429) {
        setTimeout(checkAuth, 4000);
        return;
      }
      clearStoredToken();
      setUser(false);
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    if (window.location.hash?.includes('session_id=')) {
      setLoading(false);
      return;
    }
    checkAuth();
  }, [checkAuth]);

  const login = useCallback(async (email, password) => {
    const { data } = await apiClient.post('/auth/login', { email, password });
    if (data.mfa_required) return data;
    if (data.token) setStoredToken(data.token);
    setUser(data);
    return data;
  }, []);

  const completeMfa = useCallback(async (challengeToken, code) => {
    const { data } = await apiClient.post('/auth/mfa/complete', { challenge_token: challengeToken, code });
    if (data.token) setStoredToken(data.token);
    setUser(data);
    return data;
  }, []);

  const register = useCallback(async (email, password, name, role) => {
    const { data } = await apiClient.post('/auth/register', { email, password, name, role });
    if (data.token) setStoredToken(data.token);
    setUser(data);
    return data;
  }, []);

  const logout = useCallback(async () => {
    try {
      await apiClient.post('/auth/logout');
    } catch (err) {
      console.error('Logout error:', err.message);
    }
    clearStoredToken();
    setUser(false);
  }, []);

  return (
    <AuthContext.Provider value={{ user, loading, login, completeMfa, register, logout }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);

// Axios instance with auth — sends cookies + Bearer token
export function useApi() {
  const { user } = useAuth();
  const instance = useRef(
    axios.create({ baseURL: API, withCredentials: true })
  ).current;

  useEffect(() => {
    const id = instance.interceptors.request.use((config) => {
      const token = user?.token || getStoredToken();
      if (token && token !== 'cookie') {
        config.headers.Authorization = `Bearer ${token}`;
      }
      return config;
    });
    // A read-only session gets told what happened, in words, instead of a bare 403. Every button
    // on the platform is covered by this without touching any of them.
    const rid = instance.interceptors.response.use(
      (res) => res,
      async (err) => {
        const code = err?.response?.data?.code;
        // A throttle is not an error the user can act on. One screen costs up to 37 calls, so a
        // burst used to surface as a shower of red toasts from every card on the page. Wait out
        // the window and ask again — the caller never sees it.
        if (err?.response?.status === 429 && err.config && !err.config._throttleRetry) {
          err.config._throttleRetry = true;
          await new Promise(r => setTimeout(r, 1500));
          return instance.request(err.config);
        }
        if (code === 'read_only_access') {
          toast.info('Read-only access — nothing was changed', {
            description: 'This login can see the platform but cannot alter it.',
          });
        } else if (code === 'observer_scope') {
          toast.info('Outside your access', { description: err.response.data.detail });
        } else if (err?.response?.data?.detail?.code === 'observer_expired') {
          toast.error('This access period has ended', {
            description: err.response.data.detail.detail,
          });
        }
        return Promise.reject(err);
      },
    );
    return () => {
      instance.interceptors.request.eject(id);
      instance.interceptors.response.eject(rid);
    };
  }, [user, instance]);

  return instance;
}
